// @groove/angular — public API (the Angular binding layer for Groove).
//
// These directives and components apply Groove's CSS classes and data
// attributes; they carry NO styles of their own. Every visual lives in
// @groove/styles, which is therefore a PEER dependency — a runtime requirement
// the TypeScript compiler never observes. Import it once in your application:
//
//   import '@groove/styles';
//
// Axis vocabularies (the `as const` tuples and their derived union types) are
// owned by @groove/core and re-exported through the directive that consumes
// them, so a consumer can import either from the family module or from here.
//
// Exports are named deliberately rather than wildcarded: this list IS the
// package's contract. Anything not named here — host-shell test components,
// the axe test helper, internal computed signals — is intentionally private.

// --- Element tier -----------------------------------------------------------
export {
  GrooveActionDirective,
  ACTION_SIZES,
  ACTION_TONES,
  ACTION_VARIANTS,
  type ActionSize,
  type ActionTone,
  type ActionVariant,
} from './lib/action/groove-action.directive';

export {
  GrooveBadgeDirective,
  BADGE_SIZES,
  BADGE_TONES,
  BADGE_VARIANTS,
  type BadgeSize,
  type BadgeTone,
  type BadgeVariant,
} from './lib/badge/groove-badge.directive';

export { GrooveSpinnerDirective } from './lib/spinner/groove-spinner.directive';
export type {
  SpinnerSize,
  SpinnerTone,
} from './lib/spinner/groove-spinner.directive';

export { GrooveSparklineDirective } from './lib/sparkline/groove-sparkline.directive';
export type { SparklinePoint } from './lib/sparkline/groove-sparkline.directive';

// --- Component tier ---------------------------------------------------------
export {
  GrooveSurfaceDirective,
  SURFACE_ELEVATIONS,
  SURFACE_EMPHASES,
  SURFACE_SIZES,
  SURFACE_TONES,
  SURFACE_VARIANTS,
  type SurfaceElevation,
  type SurfaceEmphasis,
  type SurfaceSize,
  type SurfaceTone,
  type SurfaceVariant,
} from './lib/surface/groove-surface.directive';

export {
  GrooveBandDirective,
  BAND_TONES,
  type BandTone,
} from './lib/band/groove-band.directive';

export {
  GrooveCalloutDirective,
  CALLOUT_TONES,
  type CalloutTone,
} from './lib/callout/groove-callout.directive';

export {
  GrooveListLinkDirective,
  LIST_LINK_SIZES,
  type ListLinkSize,
} from './lib/list-link/groove-list-link.directive';

export { GrooveListLinkGroupDirective } from './lib/list-link/groove-list-link-group.directive';
export type { ListLinkGroupGap } from './lib/list-link/groove-list-link-group.directive';

export {
  GroovePullquoteDirective,
  PULLQUOTE_SIZES,
  PULLQUOTE_VARIANTS,
  type PullquoteSize,
  type PullquoteVariant,
} from './lib/pullquote/groove-pullquote.directive';

export {
  GrooveStatStripDirective,
  STAT_STRIP_NUMERALS,
  type StatStripNumerals,
} from './lib/stat-strip/groove-stat-strip.directive';

export { GrooveDialogDirective } from './lib/dialog/groove-dialog.directive';
export type {
  DialogSize,
  DialogVariant,
} from './lib/dialog/groove-dialog.directive';

// `<groove-chart>` takes its ENTIRE input as one `data` object, so every type
// below is part of the contract a consumer has to construct — none of them is
// an internal detail. Its axis-tick and scale geometry are NOT re-exported
// here: they are framework-neutral and live in @groove/core (`AxisTick`,
// `scaleLinear`, `integerTicks`, `niceStep`), which is where a non-Angular
// consumer can also reach them.
export { GrooveChartComponent } from './lib/chart/groove-chart.component';
export type {
  ChartDash,
  ChartInput,
  ChartMarker,
  ChartPoint,
  ChartSeriesInput,
  ChartTone,
} from './lib/chart/groove-chart.component';

export { GrooveStateComponent } from './lib/state/groove-state.component';
export type { StateSize, StateTone } from './lib/state/groove-state.component';

// --- Pattern tier -----------------------------------------------------------
export {
  GrooveHeroComponent,
  HERO_LAYOUTS,
  type HeroLayout,
} from './lib/hero/groove-hero.component';

// `[grooveSpotlight]` deliberately has NO tone or variant axis: scene controls
// the palette through token re-binding, so there is no vocabulary to export.
// That absence is asserted, not assumed — see groove-spotlight.directive.spec.ts.
export { GrooveSpotlightDirective } from './lib/spotlight/groove-spotlight.directive';

// NOTE: `groove-related-tiles` is intentionally absent. It is a CSS-only layout
// pattern shipped entirely by @groove/styles — there is no directive to export.
// Its accessibility contract is still gated here, by
// lib/related-tiles/groove-related-tiles.spec.ts.
